import { useState } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@web/lib/shadcn';

import { Button } from '@web/components/ui/button';

import { Form } from '@web/components/ui/form';

import { ContractData } from '@shared/types/contract-data';

import { DoctorDataForContract, PricedItem } from '@shared/types';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@web/components/ui/dialog';
import { EditableText } from '@web/components/ui/editable-text';
import { Spinner } from '@web/components/ui/spinner';
import { toast } from 'sonner';
import { ContractDetailsView } from './contract-details-view';
import { ContractSummaryView } from './contract-summary-view';
import { PersonalInfoView } from './personal-info-view';

import { minTotalProceduresCost } from '@shared/constants';
import { ConfirmationDialog } from '@web/components/ui/confirmation-dialog';

export function EditContractForm({
  form,
  onUpdate,
  onSubmit,
  allProcedures,
  allProducts,
  allDoctors,
  editable,
}: {
  form: UseFormReturn<ContractData>;
  onUpdate?: () => Promise<void>;
  onSubmit?: () => Promise<void>;
  allProcedures: PricedItem[];
  allProducts: PricedItem[];
  allDoctors: DoctorDataForContract[];
  editable: boolean;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [step, setStep] = useState(0);

  const [submitConfirmationDialogOpen, setSubmitConfirmationDialogOpen] =
    useState(false);

  const handleSubmit = async (data: ContractData) => {
    const isValid = await form.trigger();
    if (isValid) {
      setIsSubmitting(true);
      await onSubmit?.();
      setIsSubmitting(false);

      toast.success('Contract submitted', {
        description: `for ${data.firstName} ${data.lastName} with email ${data.email}`,
      });

      setStep(0);
      form.reset();
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const nextStep = async () => {
    const view = views[step];

    const fieldsToValidate = view.fieldsToValidate;

    if (!(await form.trigger(fieldsToValidate))) return;

    const totalProceduresCost = form
      .watch('procedures')
      .reduce((acc, p) => acc + p.price, 0);

    if (
      view.key === 'doctor-procedures-products' &&
      totalProceduresCost < minTotalProceduresCost
    ) {
      toast.error(
        `Total cost of procedures must be at least ${minTotalProceduresCost.toString()}`,
      );
      return;
    }

    await onUpdate?.();

    setStep((prev) => Math.min(prev + 1, 2));
  };

  const views = [
    {
      key: 'contact-info',
      title: 'Contact Information',
      content: <PersonalInfoView key="personal-info" form={form} />,
      fieldsToValidate: ['firstName', 'lastName', 'email', 'phoneNumber'],
    },

    {
      key: 'doctor-procedures-products',
      title: 'Doctor, Procedures & Products',
      content: (
        <ContractDetailsView
          key="doctor-and-procedures"
          form={form}
          allProcedures={allProcedures}
          allProducts={allProducts}
          allDoctors={allDoctors}
        />
      ),
      fieldsToValidate: [
        'procedures',
        'products',
        'surgeonDisplayName',
        'discount',
      ],
    },

    {
      key: 'contract-summary',
      title: 'Contract Summary',
      content: <ContractSummaryView key="confirmation" form={form} />,
      fieldsToValidate: [],
    },
  ] as const satisfies {
    key: string;
    title: string;
    content: React.ReactNode;
    fieldsToValidate: (keyof ContractData)[];
  }[];

  const contractName = form.watch('name');

  const shownViews = editable ? views : views.slice(-1);

  return (
    <>
      <DialogHeader className="bg-gray-50 border-b px-6 py-4 sticky top-0 z-10">
        <DialogTitle className="text-lg font-bold text-gray-800">
          {contractName && (
            <EditableText
              initialText={contractName}
              onSave={(str) => {
                form.setValue('name', str);
              }}
            />
          )}
        </DialogTitle>
        <DialogDescription className="text-gray-600 font-medium">{shownViews[step].title}</DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form
          onSubmit={(e) => {
            // void form.handleSubmit(handleSubmit)(e);
            e.preventDefault();
            setSubmitConfirmationDialogOpen(true);
          }}
          className="flex h-full flex-col justify-stretch px-6 py-2"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ x: 10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="my-10 flex grow flex-col"
            >
              {shownViews[step].content}
            </motion.div>
          </AnimatePresence>

          <div className="sticky bottom-0 bg-white border-t py-4 px-2 mt-4 flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              className={cn(
                "rounded-full border-gray-300 hover:bg-gray-100",
                step === 0 && 'invisible'
              )}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </Button>

            {step < shownViews.length - 1 ? (
              <Button
                key={step}
                type="button"
                onClick={() => {
                  void nextStep();
                }}
                className="rounded-full bg-blue-600 hover:bg-blue-700"
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            ) : editable ? (
              <Button 
                key={step} 
                type="submit" 
                disabled={isSubmitting}
                className="rounded-full bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? <Spinner /> : 'Send for approval'}
              </Button>
            ) : null}
          </div>
        </form>
      </Form>

      <ConfirmationDialog
        open={submitConfirmationDialogOpen}
        onOpenChange={setSubmitConfirmationDialogOpen}
        title="Send for approval"
        description="You are about to send this contract for approval. Are you sure?"
        confirmLabel="Send"
        destructive={false}
        onConfirm={() => {
          void handleSubmit(form.getValues());
          setSubmitConfirmationDialogOpen(false);
        }}
        onCancel={() => {
          setSubmitConfirmationDialogOpen(false);
        }}
      />
    </>
  );
}
